import { useEffect, useState } from 'react';
import type { PublicKey } from '../api';
import { UnsupportedBrowserError, fingerprintOfPublicKey, generateEd25519KeyPair } from '../ssh-key';

export function KeyList({
  keys,
  onRemove,
}: {
  keys: PublicKey[];
  onRemove?: (keyId: string) => void;
}) {
  if (keys.length === 0) return <p className="hint">No public keys.</p>;
  return (
    <ul className="keys">
      {keys.map((entry) => (
        <KeyRow key={entry.id} entry={entry} onRemove={onRemove} />
      ))}
    </ul>
  );
}

function KeyRow({ entry, onRemove }: { entry: PublicKey; onRemove?: (keyId: string) => void }) {
  const [fingerprint, setFingerprint] = useState<string>();
  useEffect(() => {
    void fingerprintOfPublicKey(entry.key).then(setFingerprint);
  }, [entry.key]);

  return (
    <li>
      <div>
        <strong>{entry.label || entry.key.split(/\s+/).slice(2).join(' ') || 'unlabelled'}</strong>
        {/* The fingerprint, not the key, is what someone compares against
            `ssh-keygen -lf` on their own machine. */}
        <code className="fingerprint">{fingerprint ?? '…'}</code>
        <span className="hint">added {new Date(entry.addedAt).toLocaleDateString()}</span>
      </div>
      {onRemove && (
        <button type="button" className="danger link" onClick={() => onRemove(entry.id)}>
          Revoke
        </button>
      )}
    </li>
  );
}

// Adding a key, either by pasting one or by generating it here.
//
// Generation happens in the browser and the private key is offered as a
// download and then dropped. Nothing sends it anywhere, and there is no second
// chance to retrieve it -- which is stated plainly, because the alternative is
// someone closing the tab and assuming it can be re-downloaded.
export function AddKeyForm({
  onAdd,
  defaultComment,
  generateNote,
}: {
  onAdd: (body: { key: string; label?: string }) => Promise<unknown>;
  defaultComment: string;
  // Set when the person using this form is not the person who will use the key,
  // i.e. an admin generating on a partner's behalf. The private key lands on
  // *this* machine, which is a fact worth stating rather than implying.
  generateNote?: string;
}) {
  const [mode, setMode] = useState<'paste' | 'generate'>('paste');
  const [pasted, setPasted] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [generated, setGenerated] = useState<{ fingerprint: string; downloaded: boolean }>();

  const submitPasted = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await onAdd({ key: pasted.trim(), label: label.trim() || undefined });
      setPasted('');
      setLabel('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const pair = await generateEd25519KeyPair(defaultComment);

      // Downloaded before the public half is registered. If the download is
      // blocked or cancelled, no key gets added, which is better than a
      // registered key whose private half is already gone.
      download(`${safeFileName(defaultComment)}_ed25519`, pair.privateKey);
      download(`${safeFileName(defaultComment)}_ed25519.pub`, `${pair.publicKey}\n`);

      await onAdd({ key: pair.publicKey, label: label.trim() || 'generated in browser' });
      setGenerated({ fingerprint: pair.fingerprint, downloaded: true });
      setLabel('');
    } catch (err) {
      setError(
        err instanceof UnsupportedBrowserError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err)
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="add-key">
      <div className="segmented">
        <button
          type="button"
          className={mode === 'paste' ? 'active' : undefined}
          onClick={() => setMode('paste')}
        >
          Paste a public key
        </button>
        <button
          type="button"
          className={mode === 'generate' ? 'active' : undefined}
          onClick={() => setMode('generate')}
        >
          Generate a new keypair
        </button>
      </div>

      <label>
        Label (optional)
        <input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="laptop, batch server, …"
        />
      </label>

      {mode === 'paste' ? (
        <>
          <label>
            Public key
            <textarea
              rows={3}
              value={pasted}
              onChange={(event) => setPasted(event.target.value)}
              placeholder="ssh-ed25519 AAAAC3Nza… user@host"
              spellCheck={false}
            />
          </label>
          <p className="hint">
            The contents of your <code>.pub</code> file. Never paste a private key here — if you
            do, treat it as compromised and generate a new one.
          </p>
          <button type="button" onClick={() => void submitPasted()} disabled={busy || !pasted.trim()}>
            {busy ? 'Adding…' : 'Add key'}
          </button>
        </>
      ) : (
        <>
          <p className="hint">
            The keypair is created in this browser. The private key is downloaded to this device and
            never sent to us, so it cannot be recovered if you lose it.
          </p>
          {generateNote && <p className="warn">{generateNote}</p>}
          <button type="button" onClick={() => void generate()} disabled={busy}>
            {busy ? 'Generating…' : 'Generate and download'}
          </button>
          {generated && (
            <div className="notice">
              <p>
                Key added. Two files were downloaded: the private key and its <code>.pub</code>.
              </p>
              <p>
                Fingerprint <code>{generated.fingerprint}</code>
              </p>
              <p className="hint">
                On macOS or Linux, move the private key into <code>~/.ssh/</code> and run{' '}
                <code>chmod 600</code> on it. To add a passphrase, run{' '}
                <code>ssh-keygen -p -f &lt;file&gt;</code>.
              </p>
            </div>
          )}
        </>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

// A blob download rather than a data: URL, because the CSP forbids navigating to
// one and a private key does not belong in a URL the browser might record.
export const download = (filename: string, contents: string) => {
  const url = URL.createObjectURL(new Blob([contents], { type: 'application/octet-stream' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

export const safeFileName = (value: string) => value.replace(/[^a-zA-Z0-9._-]+/g, '_') || 'sftp';
